import { useLayoutEffect } from "react";

export const scrollRestoreStorageKey = (pathname: string) => `scroll-restore:${pathname}`;

// Ojo: /feed (conciertos) tiene su propia lógica de posicionamiento y no debe
// mezclarse con la restauración global para evitar parpadeos/estados "invisible".
const PATHS_WITH_SCROLL_RESTORE = new Set(["/articulos", "/blogs", "/playlists"]);

export const shouldPersistScrollRestore = (pathname: string): boolean => PATHS_WITH_SCROLL_RESTORE.has(pathname);

const instantScrollTo = (top: number) => {
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";
  window.scrollTo(0, top);
  requestAnimationFrame(() => {
    root.style.scrollBehavior = previousBehavior;
  });
};

export function readSavedScrollY(pathname: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(scrollRestoreStorageKey(pathname));
    if (raw == null) return null;
    const y = Number.parseInt(raw, 10);
    if (!Number.isFinite(y) || y < 0) return null;
    return y;
  } catch {
    return null;
  }
}

/**
 * Restaura el scroll de forma estable (reintentos) para aguantar reflows por imágenes,
 * infinite scroll y cambios de layout tras la primera pintura.
 */
export function restoreWindowScrollStable(targetY: number): () => void {
  if (typeof window === "undefined") return () => {};
  const y = Math.max(0, Math.round(targetY));

  let cancelled = false;
  const timeouts: number[] = [];
  let resizeBumps = 0;
  const maxResizeBumps = 6;

  const attempt = () => {
    if (cancelled) return;
    instantScrollTo(y);
  };

  // Intento inmediato + reintentos discretos (sin bucles) para evitar que “se vea” el ajuste.
  attempt();
  for (const ms of [0, 50, 150, 350]) {
    const id = window.setTimeout(() => {
      if (cancelled) return;
      // Sólo reintentar si nos hemos desviado del objetivo (reflow / imágenes).
      if (Math.abs(window.scrollY - y) > 2) attempt();
    }, ms);
    timeouts.push(id);
  }

  // Si el documento crece después (lazy images / infinite), reintentar unas pocas veces.
  const ro = new ResizeObserver(() => {
    if (cancelled) return;
    if (resizeBumps >= maxResizeBumps) return;
    resizeBumps += 1;
    if (Math.abs(window.scrollY - y) <= 2) return;
    const id = window.setTimeout(() => {
      if (cancelled) return;
      if (Math.abs(window.scrollY - y) > 2) attempt();
    }, 0);
    timeouts.push(id);
  });
  try {
    ro.observe(document.body);
  } catch {
    /* ignore */
  }

  return () => {
    cancelled = true;
    for (const id of timeouts) window.clearTimeout(id);
    ro.disconnect();
  };
}

/** Guarda window.scrollY mientras la ruta está montada (throttled). useLayoutEffect para que el cleanup al salir guarde antes del scroll a 0 del AppShell. */
export function usePersistWindowScroll(pathname: string): void {
  useLayoutEffect(() => {
    const key = scrollRestoreStorageKey(pathname);
    // Importante: en unmount no podemos fiarnos de window.scrollY porque otra lógica
    // (p.ej. scroll-to-top global) puede haberlo cambiado ya. Guardamos el último valor
    // observado durante la vida del componente.
    let lastY = Math.round(window.scrollY);
    const save = () => {
      try {
        sessionStorage.setItem(key, String(lastY));
      } catch {
        /* ignore */
      }
    };
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      lastY = Math.round(window.scrollY);
      if (timeoutId != null) return;
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        lastY = Math.round(window.scrollY);
        save();
      }, 120);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // No guardar en mount: scrollY suele ser 0 o el de la página anterior antes de que App restaure POP.
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timeoutId != null) window.clearTimeout(timeoutId);
      lastY = Math.round(lastY || window.scrollY);
      save();
    };
  }, [pathname]);
}
